import {useEffect, useMemo, useState} from 'react';

import type {MeetingLocationType as LocationType} from 'nutshell-graphql-types';

type DefaultValues = {
    [key: string]: string;
};

export type SchedulerConfig = {
    id: string;
    meetingSlug: string;
    name: string;
    description: string;
    durationMinutes: number;
    locationType: LocationType;
    user: {
        id: string;
        name: string;
    };
    timezone: string;
    availabilityStartTimes: number[];
    formConfig?: any | null | undefined;
    booking?:
        | {
              startTime: number;
          }
        | null
        | undefined;
};

export function useGetPeanutSchedulerConfig(
    meetingModelId?: number | null,
    instanceId?: string | null,
    isPreview?: boolean,
    defaultValues?: DefaultValues
): {
    schedulerConfig: SchedulerConfig | null | undefined;
    error: boolean;
    refetch: () => void;
} {
    const [refetch, setRefetch] = useState<boolean>(false);
    const [schedulerConfig, setSchedulerConfig] = useState<SchedulerConfig | null | undefined>(
        undefined
    );
    const [error, setError] = useState<boolean>(false);

    const allDefaultValues = useMemo(
        () => ({
            preview: isPreview || true,
            ...defaultValues,
        }),
        [isPreview, defaultValues]
    );

    useEffect(() => {
        if (instanceId && meetingModelId && (!schedulerConfig || refetch)) {
            // Fetch the calendar for given instance and calendar id
            // @ts-expect-error
            fetchSchedulerConfig(instanceId, meetingModelId, allDefaultValues)
                .then((response) => {
                    const {meeting, formConfig} = response;
                    const newConfig: SchedulerConfig | null | undefined = {
                        ...meeting,
                        formConfig: formConfig,
                    };

                    if (newConfig) {
                        setSchedulerConfig(newConfig);
                        setRefetch(false);
                    } else {
                        setError(true);
                    }
                })
                .catch(() => {
                    setError(true);
                });
        }
    }, [instanceId, meetingModelId, schedulerConfig, refetch, allDefaultValues]);

    return {
        schedulerConfig,
        error,
        refetch: () => setRefetch(true),
    };
}

function fetchSchedulerConfig(
    instanceId: string,
    meetingModelId: number,
    defaultValues: DefaultValues
) {
    let schedulerGetConfigUrl = `/scheduler-public/meeting/${instanceId}/${meetingModelId}`;

    schedulerGetConfigUrl =
        Object.keys(defaultValues).length > 0
            ? getSchedulerGetConfigUrlWithDefaultValues(schedulerGetConfigUrl, defaultValues)
            : schedulerGetConfigUrl;

    return fetch(schedulerGetConfigUrl)
        .then((response) => response.json())
        .then((data) => data);
}

function getSchedulerGetConfigUrlWithDefaultValues(
    schedulerGetConfigUrl: string,
    defaultValues: DefaultValues
): string {
    const defaultValuesString = new URLSearchParams(defaultValues).toString();

    if (schedulerGetConfigUrl.includes('?')) {
        return `${schedulerGetConfigUrl}&${defaultValuesString}`;
    }

    return `${schedulerGetConfigUrl}?${defaultValuesString}`;
}
